import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import {
  Badge,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap'
import { updateSolicitation } from '../../../store/ducks/humanResources'
import { notifyError, notifySuccess } from '../../../utils/notify'
import { mmYyyyFromMesRef } from '../../../utils/monthSelectOptions'
import {
  fetchExtratoRhMes,
  fetchExtratoRhHistorico,
  fetchBancoAcumulado,
  fecharPontoIndividual,
  cancelarPontoIndividual,
  registrarFeriasColaborador,
  registrarFeriadoColaborador,
  aprovarPendenciasMes,
  verificaSobreposicaoAusencia,
  registrarAtestadoColaborador,
  fetchListaProjetos,
  fetchListaAtividades,
  registrarAjustePonto,
} from './rhDashboardApi'
import { validateCargaHhMm } from './rhFechamentoCarga'

const HOUR_MINUTE_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/

const normalizeTempo = (t) => {
  const parts = (t || '').trim().split(':')
  if (parts.length === 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`
  }
  if (parts.length === 3) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`
  }
  return '00:00:00'
}

const emptyAjusteAtivRow = (id) => ({
  id,
  projeto_id: '',
  atividade: '',
  descricao: '',
  tempo: '08:00:00',
  atividadesOpts: [],
})

/** Converte banco_aumulado da API (HH:MM:SS) para exibição (HhMMmin). */
const formatBancoAumuladoApi = (valor) => {
  if (!valor || typeof valor !== 'string') return null
  const neg = valor.startsWith('-')
  const parts = valor.replace(/^-/, '').split(':')
  if (parts.length < 2) return null
  const h = Number(parts[0])
  const m = Number(parts[1])
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return `${neg ? '-' : ''}${h}h${String(m).padStart(2, '0')}min`
}

const parseBancoAumuladoApiSec = (valor) => {
  if (!valor || typeof valor !== 'string') return null
  const neg = valor.startsWith('-')
  const parts = valor.replace(/^-/, '').split(':')
  if (parts.length < 2) return null
  const h = Number(parts[0])
  const m = Number(parts[1])
  const s = Number(parts[2] || 0)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  const total = h * 3600 + m * 60 + (Number.isFinite(s) ? s : 0)
  return neg ? -total : total
}

const DIVERGENCIA_TOLERANCIA_S = 59

const formatRhDuration = (seconds) => {
  const totalSeconds = Number(seconds)
  if (!Number.isFinite(totalSeconds)) return null
  const sign = totalSeconds < 0 ? '-' : ''
  const abs = Math.abs(totalSeconds)
  const hours = Math.floor(abs / 3600)
  const minutes = Math.floor((abs % 3600) / 60)
  return `${sign}${hours}h${String(minutes).padStart(2, '0')}min`
}

const resolveSaldoCalculadoS = (mes) => {
  if (mes?.saldo_live_s != null) return Number(mes.saldo_live_s)
  const abono = Number(mes.abono_s) || 0
  const realizada = Number(mes.carga_realizada_live_s) || 0
  const carga = mes?.fechamento_ativo
    ? (Number(mes.carga_contratada_hon_s ?? mes.carga_contratada_s) || 0)
    : (Number(mes.carga_contratada_vigente_s ?? mes.carga_contratada_s) || 0)
  return realizada + abono - carga
}

const resolveSaldoCalculadoFmt = (mes) => formatRhDuration(resolveSaldoCalculadoS(mes))

const divergemComTolerancia = (a, b) => {
  if (a == null || b == null) return false
  return Math.abs(Number(a) - Number(b)) > DIVERGENCIA_TOLERANCIA_S
}

const saldosDivergem = (mes) => (
  divergemComTolerancia(mes?.saldo_mes_s, mes?.saldo_calculado_s)
)

const bancosDivergem = (mes) => (
  divergemComTolerancia(mes?.banco_encerramento_s, mes?.banco_calculado_s)
)

const saldoDivergenciaStyle = (diverge) => (
  diverge
    ? { backgroundColor: '#fff3cd', fontWeight: 700, borderRadius: 4, padding: '2px 4px' }
    : undefined
)

const enrichHistoricoMeses = (historico, bancoPorMes) => {
  const meses = Array.isArray(historico?.meses) ? historico.meses : []
  if (!meses.length) return historico

  const mesesAsc = [...meses].sort((a, b) => String(a.mes_ref).localeCompare(String(b.mes_ref)))
  let acumCalculado = 0
  const calculadoPorMes = new Map()

  mesesAsc.forEach((mes) => {
    acumCalculado += resolveSaldoCalculadoS(mes)
    calculadoPorMes.set(String(mes.mes_ref), acumCalculado)
  })

  return {
    ...historico,
    meses: meses.map((mes) => {
      const bancoCalculadoS = calculadoPorMes.get(String(mes.mes_ref))
      const bancoEncerramentoS = parseBancoAumuladoApiSec(bancoPorMes?.[mes.mes]?.banco_aumulado)
      return {
        ...mes,
        saldo_calculado_s: resolveSaldoCalculadoS(mes),
        saldo_calculado_fmt: resolveSaldoCalculadoFmt(mes),
        banco_encerramento_s: bancoEncerramentoS,
        banco_encerramento_fmt: formatBancoAumuladoApi(bancoPorMes?.[mes.mes]?.banco_aumulado),
        banco_calculado_s: bancoCalculadoS,
        banco_calculado_fmt: formatRhDuration(bancoCalculadoS),
      }
    }),
  }
}

const mrtCompactProps = {
  muiTableBodyCellProps: { sx: { fontSize: '11px' } },
  muiTableHeadCellProps: {
    sx: {
      '.MuiSvgIcon-root': { fontSize: '18px' },
      fontSize: '12px',
      fontWeight: 'normal',
      padding: '8px',
    },
  },
  muiTablePaginationProps: {
    sx: {
      '.MuiTablePagination-displayedRows': { fontSize: '12px' },
      marginTop: '0px',
      fontSize: '12px',
    },
    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Dias por página</span>,
    rowsPerPageOptions: [10, 31, 50],
  },
  enableDensityToggle: false,
  enableColumnFilters: false,
  positionPagination: 'bottom',
}

const RESUMO_CARDS = [
  { key: 'horas_atividade_fmt', label: 'Horas em atividade' },
  { key: 'horas_abono_fmt', label: 'Abono' },
  { key: 'carga_horaria_fmt', label: 'Carga horária contratada' },
  { key: 'banco_acumulado_fmt', label: 'Banco acumulado' },
]

const formatAprovBadge = (aprov) => {
  switch (Number(aprov)) {
    case 0:
      return <Badge color="warning" pill>Em aberto</Badge>
    case 1:
      return <Badge color="success" pill>Aprovada</Badge>
    case 2:
      return <Badge color="danger" pill>Recusada</Badge>
    default:
      return null
  }
}

const ResumoCards = ({ totais, loading }) => (
  <Row className="mb-3">
    {RESUMO_CARDS.map(item => (
      <Col key={item.key} md={3} sm={6} className="mb-2">
        <Card className="mb-0 border">
          <CardBody className="py-2 px-3">
            <div className="text-muted small">{item.label}</div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {loading ? '…' : (totais[item.key] || '—')}
            </div>
          </CardBody>
        </Card>
      </Col>
    ))}
  </Row>
)

const RhExtratoPanel = ({
  colaborador,
  initialMesRef,
  onRefresh,
  updateSolicitation,
  updateSolicitationIsLoading,
}) => {
  const [extratoMesRef, setExtratoMesRef] = useState(initialMesRef)
  const [extrato, setExtrato] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [viewMode, setViewMode] = useState('extrato')
  const [historico, setHistorico] = useState(null)
  const [historicoLoading, setHistoricoLoading] = useState(false)
  const [historicoError, setHistoricoError] = useState(null)
  const [feriasModalOpen, setFeriasModalOpen] = useState(false)
  const [feriasIni, setFeriasIni] = useState('')
  const [feriasFim, setFeriasFim] = useState('')
  const [feriasJustif, setFeriasJustif] = useState('')
  const [feriadoModalOpen, setFeriadoModalOpen] = useState(false)
  const [feriadoData, setFeriadoData] = useState('')
  const [feriadoNome, setFeriadoNome] = useState('Feriado')
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectReasonError, setRejectReasonError] = useState('')
  const [atestadoModalOpen, setAtestadoModalOpen] = useState(false)
  const [atestadoIni, setAtestadoIni] = useState('')
  const [atestadoFim, setAtestadoFim] = useState('')
  const [atestadoHoraIni, setAtestadoHoraIni] = useState('')
  const [atestadoHoraFim, setAtestadoHoraFim] = useState('')
  const [atestadoJustif, setAtestadoJustif] = useState('')
  const [atestadoFiles, setAtestadoFiles] = useState([])
  const [ajusteModalOpen, setAjusteModalOpen] = useState(false)
  const [ajusteData, setAjusteData] = useState('')
  const [ajusteEntrada, setAjusteEntrada] = useState('08:00')
  const [ajusteSaida, setAjusteSaida] = useState('17:00')
  const [ajusteJustif, setAjusteJustif] = useState('')
  const [ajusteProjetos, setAjusteProjetos] = useState([])
  const [ajusteAtivRows, setAjusteAtivRows] = useState([emptyAjusteAtivRow(1)])
  const [ajusteNextRowId, setAjusteNextRowId] = useState(2)
  const [fecharCargaModalOpen, setFecharCargaModalOpen] = useState(false)
  const [fecharCargaUsarReduzida, setFecharCargaUsarReduzida] = useState(false)
  const [fecharCargaHhmm, setFecharCargaHhmm] = useState('')
  const [fecharCargaErro, setFecharCargaErro] = useState('')
  const [fecharCargaPendingSobreescreve, setFecharCargaPendingSobreescreve] = useState(false)

  useEffect(() => {
    setExtratoMesRef(initialMesRef)
    setViewMode('extrato')
    setHistorico(null)
  }, [initialMesRef, colaborador?.pf_id])

  const mesMmYyyy = useMemo(
    () => mmYyyyFromMesRef(extratoMesRef || initialMesRef),
    [extratoMesRef, initialMesRef],
  )

  const defaultDiaNoMes = useCallback(() => {
    const ref = extratoMesRef || initialMesRef
    const m = moment(ref, 'YYYY-MM', true)
    const hoje = moment()
    if (hoje.isSame(m, 'month')) return hoje.format('YYYY-MM-DD')
    return m.startOf('month').format('YYYY-MM-DD')
  }, [extratoMesRef, initialMesRef])

  const atestadoMesmoDia = atestadoIni && atestadoFim && atestadoIni === atestadoFim

  const loadExtrato = useCallback(async () => {
    if (!colaborador?.pf_graphId || !extratoMesRef) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchExtratoRhMes(colaborador.pf_graphId, mesMmYyyy)
      if (data?.ok === false) {
        setExtrato(null)
        setError(data.msg || 'Erro ao carregar extrato.')
      } else {
        setExtrato(data)
      }
    } catch (err) {
      setExtrato(null)
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }, [colaborador?.pf_graphId, extratoMesRef, mesMmYyyy])

  const loadHistorico = useCallback(async () => {
    if (!colaborador?.pf_graphId) return
    setHistoricoLoading(true)
    setHistoricoError(null)
    try {
      const [data, bancoPorMes] = await Promise.all([
        fetchExtratoRhHistorico(colaborador.pf_graphId),
        fetchBancoAcumulado(colaborador.pf_graphId),
      ])
      if (data?.ok === false) {
        setHistorico(null)
        setHistoricoError(data.msg || 'Erro ao carregar histórico.')
      } else {
        setHistorico(enrichHistoricoMeses(data, bancoPorMes))
      }
    } catch (err) {
      setHistorico(null)
      setHistoricoError(String(err))
    } finally {
      setHistoricoLoading(false)
    }
  }, [colaborador?.pf_graphId])

  useEffect(() => {
    loadExtrato()
  }, [loadExtrato])

  useEffect(() => {
    if (viewMode === 'historico' && !historico && !historicoLoading) {
      loadHistorico()
    }
  }, [viewMode, historico, historicoLoading, loadHistorico])

  const shiftMes = (delta) => {
    const next = moment(extratoMesRef, 'YYYY-MM').add(delta, 'month').format('YYYY-MM')
    setExtratoMesRef(next)
    setViewMode('extrato')
  }

  const goToMes = (mesRef) => {
    setExtratoMesRef(mesRef)
    setViewMode('extrato')
  }

  const handleAprovarAtiv = (ativ) => {
    updateSolicitation({
      type: 1,
      solicitation: {
        pend_tab: 'pontoAtiv',
        pend_id: ativ.pontoAtiv_id,
        reason: '',
      },
      simple: true,
    })
    setTimeout(() => {
      loadExtrato()
      if (onRefresh) onRefresh()
    }, 800)
  }

  const openRejectAtiv = (ativ) => {
    setRejectTarget(ativ)
    setRejectReason('')
    setRejectReasonError('')
    setRejectOpen(true)
  }

  const handleRejectConfirm = () => {
    if (!rejectReason.trim()) {
      setRejectReasonError('Campo obrigatório.')
      return
    }
    updateSolicitation({
      type: 2,
      solicitation: {
        pend_tab: 'pontoAtiv',
        pend_id: rejectTarget.pontoAtiv_id,
        reason: rejectReason.trim(),
      },
      simple: true,
    })
    setRejectOpen(false)
    setRejectTarget(null)
    setTimeout(() => {
      loadExtrato()
      if (onRefresh) onRefresh()
    }, 800)
  }

  const handleRegistrarFerias = async () => {
    if (!feriasIni || !feriasFim) {
      notifyError('Informe data inicial e final.')
      return
    }
    if (moment(feriasFim).isBefore(moment(feriasIni))) {
      notifyError('Data final deve ser igual ou posterior à inicial.')
      return
    }
    setActionLoading(true)
    try {
      await registrarFeriasColaborador(
        colaborador.pf_graphId,
        `${feriasIni} 00:00:00`,
        `${feriasFim} 23:59:59`,
        feriasJustif || 'Férias registradas via Dashboard RH',
      )
      notifySuccess('Férias registradas.')
      setFeriasModalOpen(false)
      setFeriasIni('')
      setFeriasFim('')
      setFeriasJustif('')
      loadExtrato()
      setHistorico(null)
      if (onRefresh) onRefresh()
    } catch (err) {
      notifyError(String(err))
    } finally {
      setActionLoading(false)
    }
  }

  const openFeriadoModal = () => {
    setFeriadoData(defaultDiaNoMes())
    setFeriadoNome('Feriado')
    setFeriadoModalOpen(true)
  }

  const handleRegistrarFeriado = async () => {
    if (!feriadoData) {
      notifyError('Informe a data do feriado.')
      return
    }
    setActionLoading(true)
    try {
      await registrarFeriadoColaborador(
        colaborador.pf_graphId,
        feriadoData,
        feriadoNome.trim() || 'Feriado',
      )
      notifySuccess('Feriado registrado.')
      setFeriadoModalOpen(false)
      setFeriadoData('')
      setFeriadoNome('Feriado')
      loadExtrato()
      setHistorico(null)
      if (onRefresh) onRefresh()
    } catch (err) {
      notifyError(String(err))
    } finally {
      setActionLoading(false)
    }
  }

  const openAtestadoModal = () => {
    const dia = defaultDiaNoMes()
    setAtestadoIni(dia)
    setAtestadoFim(dia)
    setAtestadoHoraIni('')
    setAtestadoHoraFim('')
    setAtestadoJustif('')
    setAtestadoFiles([])
    setAtestadoModalOpen(true)
  }

  const handleRegistrarAtestado = async () => {
    if (!atestadoIni || !atestadoFim) {
      notifyError('Informe data inicial e final.')
      return
    }
    if (moment(atestadoFim).isBefore(moment(atestadoIni))) {
      notifyError('Data final deve ser igual ou posterior à inicial.')
      return
    }
    if (!atestadoJustif.trim()) {
      notifyError('Informe a justificativa.')
      return
    }
    if (atestadoMesmoDia) {
      if (!atestadoHoraIni || !atestadoHoraFim) {
        notifyError('Informe hora inicial e final.')
        return
      }
      if (!HOUR_MINUTE_REGEX.test(atestadoHoraIni) || !HOUR_MINUTE_REGEX.test(atestadoHoraFim)) {
        notifyError('Horário inválido.')
        return
      }
      const iniMin = Number(atestadoHoraIni.split(':')[0]) * 60 + Number(atestadoHoraIni.split(':')[1])
      const fimMin = Number(atestadoHoraFim.split(':')[0]) * 60 + Number(atestadoHoraFim.split(':')[1])
      if (fimMin <= iniMin) {
        notifyError('Hora final deve ser posterior à inicial.')
        return
      }
    }

    setActionLoading(true)
    try {
      const sobreposicao = await verificaSobreposicaoAusencia(colaborador.pf_graphId, {
        dataEntrada: atestadoIni,
        dataSaida: atestadoFim,
        horaEntrada: atestadoMesmoDia ? atestadoHoraIni : undefined,
        horaSaida: atestadoMesmoDia ? atestadoHoraFim : undefined,
      })
      if (sobreposicao?.sobrepoe === true) {
        notifyError(sobreposicao?.msg || 'Período já registrado para este colaborador.')
        return
      }

      await registrarAtestadoColaborador(colaborador.pf_graphId, {
        dataEntrada: atestadoIni,
        dataSaida: atestadoFim,
        horaEntrada: atestadoMesmoDia ? atestadoHoraIni : undefined,
        horaSaida: atestadoMesmoDia ? atestadoHoraFim : undefined,
        justificativa: atestadoJustif.trim(),
        files: atestadoFiles,
      })
      notifySuccess('Atestado registrado. Aguarde aprovação se necessário.')
      setAtestadoModalOpen(false)
      loadExtrato()
      setHistorico(null)
      if (onRefresh) onRefresh()
    } catch (err) {
      notifyError(String(err))
    } finally {
      setActionLoading(false)
    }
  }

  const openAjusteModal = async () => {
    const dia = defaultDiaNoMes()
    setAjusteData(dia)
    setAjusteEntrada('08:00')
    setAjusteSaida('17:00')
    setAjusteJustif('')
    setAjusteAtivRows([emptyAjusteAtivRow(1)])
    setAjusteNextRowId(2)
    setAjusteModalOpen(true)
    try {
      const projetos = await fetchListaProjetos(colaborador.pf_graphId)
      setAjusteProjetos(Array.isArray(projetos) ? projetos : [])
    } catch (err) {
      setAjusteProjetos([])
      notifyError(`Erro ao carregar projetos: ${err}`)
    }
  }

  const loadAtividadesAjusteRow = async (rowId, projetoId) => {
    if (!projetoId) {
      setAjusteAtivRows(rows => rows.map(r => (
        r.id === rowId ? { ...r, projeto_id: '', atividade: '', atividadesOpts: [] } : r
      )))
      return
    }
    try {
      const atividades = await fetchListaAtividades(colaborador.pf_graphId, projetoId)
      const opts = Array.isArray(atividades) ? atividades : []
      setAjusteAtivRows(rows => rows.map(r => (
        r.id === rowId ? { ...r, projeto_id: projetoId, atividade: opts[0] || '', atividadesOpts: opts } : r
      )))
    } catch (err) {
      notifyError(`Erro ao carregar atividades: ${err}`)
    }
  }

  const updateAjusteRow = (rowId, patch) => {
    setAjusteAtivRows(rows => rows.map(r => (r.id === rowId ? { ...r, ...patch } : r)))
  }

  const addAjusteRow = () => {
    setAjusteAtivRows(rows => [...rows, emptyAjusteAtivRow(ajusteNextRowId)])
    setAjusteNextRowId(id => id + 1)
  }

  const removeAjusteRow = (rowId) => {
    setAjusteAtivRows(rows => {
      if (rows.length <= 1) {
        return [emptyAjusteAtivRow(rowId)]
      }
      return rows.filter(r => r.id !== rowId)
    })
  }

  const handleRegistrarAjuste = async () => {
    if (!ajusteData || !ajusteEntrada || !ajusteSaida) {
      notifyError('Informe data, entrada e saída.')
      return
    }
    if (!ajusteJustif.trim()) {
      notifyError('Informe a justificativa.')
      return
    }
    if (!HOUR_MINUTE_REGEX.test(ajusteEntrada) || !HOUR_MINUTE_REGEX.test(ajusteSaida)) {
      notifyError('Horário de entrada/saída inválido.')
      return
    }
    const entMin = Number(ajusteEntrada.split(':')[0]) * 60 + Number(ajusteEntrada.split(':')[1])
    const saiMin = Number(ajusteSaida.split(':')[0]) * 60 + Number(ajusteSaida.split(':')[1])
    if (saiMin <= entMin) {
      notifyError('Saída deve ser posterior à entrada.')
      return
    }

    const items = ajusteAtivRows
      .filter(r => r.projeto_id || r.atividade || r.descricao || r.tempo)
      .map(r => ({
        projeto_id: r.projeto_id,
        atividade: r.atividade,
        descricao: r.descricao,
        tempo: normalizeTempo(r.tempo),
      }))

    if (!items.length || items.some(i => !i.projeto_id)) {
      notifyError('Inclua ao menos uma atividade com setor/projeto.')
      return
    }

    setActionLoading(true)
    try {
      await registrarAjustePonto(colaborador.pf_graphId, {
        data: ajusteData,
        dataEntrada: `${ajusteData} ${ajusteEntrada}:00`,
        dataSaida: `${ajusteData} ${ajusteSaida}:00`,
        justificativa: ajusteJustif.trim(),
        items,
      })
      notifySuccess('Ajuste de ponto registrado.')
      setAjusteModalOpen(false)
      loadExtrato()
      setHistorico(null)
      if (onRefresh) onRefresh()
    } catch (err) {
      notifyError(String(err))
    } finally {
      setActionLoading(false)
    }
  }

  const executarFechamento = async (sobreescreve, cargaHorariaHhmm = null) => {
    const msg = sobreescreve
      ? `Reprocessar fechamento de ${mesMmYyyy} para ${colaborador.pf_nomeExibicao}?`
      : `Fechar ponto de ${mesMmYyyy} para ${colaborador.pf_nomeExibicao}?`
    if (!window.confirm(msg)) return
    setActionLoading(true)
    try {
      const data = await fecharPontoIndividual(
        colaborador.pf_graphId,
        mesMmYyyy,
        sobreescreve,
        cargaHorariaHhmm,
      )
      if (data?.ok === false) throw new Error(data.msg || 'Erro ao fechar ponto.')
      notifySuccess(data.msg || 'Fechamento concluído.')
      setFecharCargaModalOpen(false)
      loadExtrato()
      setHistorico(null)
      if (onRefresh) onRefresh()
    } catch (err) {
      notifyError(String(err))
    } finally {
      setActionLoading(false)
    }
  }

  const runFechamento = async (sobreescreve) => {
    const info = extrato?.totais?.primeira_folha_carga_reduzida
    if (info?.elegivel) {
      setFecharCargaPendingSobreescreve(sobreescreve)
      setFecharCargaUsarReduzida(false)
      setFecharCargaHhmm('')
      setFecharCargaErro('')
      setFecharCargaModalOpen(true)
      return
    }
    await executarFechamento(sobreescreve, null)
  }

  const confirmarFechamentoComCarga = async () => {
    const info = extrato?.totais?.primeira_folha_carga_reduzida
    const maxHhmm = info?.carga_contratual_hhmm
    if (fecharCargaUsarReduzida) {
      const erro = validateCargaHhMm(fecharCargaHhmm, maxHhmm)
      if (erro) {
        setFecharCargaErro(erro)
        return
      }
      await executarFechamento(fecharCargaPendingSobreescreve, fecharCargaHhmm.trim())
      return
    }
    await executarFechamento(fecharCargaPendingSobreescreve, null)
  }

  const runCancelarFechamento = async () => {
    if (!window.confirm(`Cancelar fechamento de ${mesMmYyyy} para ${colaborador.pf_nomeExibicao}?`)) return
    setActionLoading(true)
    try {
      const data = await cancelarPontoIndividual(colaborador.pf_graphId, mesMmYyyy)
      if (data?.ok === false) throw new Error(data.msg || 'Erro ao cancelar fechamento.')
      notifySuccess(data.msg || 'Fechamento cancelado.')
      loadExtrato()
      setHistorico(null)
      if (onRefresh) onRefresh()
    } catch (err) {
      notifyError(String(err))
    } finally {
      setActionLoading(false)
    }
  }

  const dias = extrato?.dias || []
  const totais = extrato?.totais || {}
  const fechamentoAtivo = Boolean(totais.fechamento_ativo)
  const pendentesMes = Number(totais.pendentes_mes || 0)

  const handleAprovarTodasPendencias = async () => {
    if (!colaborador?.pf_id) return
    if (pendentesMes <= 0) {
      notifyError('Não há pendências em aberto neste mês.')
      return
    }
    const msg = `Aprovar ${pendentesMes} pendência(s) de ${mesMmYyyy} para ${colaborador.pf_nomeExibicao}?`
    if (!window.confirm(msg)) return

    setActionLoading(true)
    try {
      const data = await aprovarPendenciasMes(colaborador.pf_id, mesMmYyyy)
      if (data?.ok === false) throw new Error(data.msg || 'Erro ao aprovar pendências.')
      notifySuccess(data.msg || 'Pendências aprovadas.')
      loadExtrato()
      setHistorico(null)
      if (onRefresh) onRefresh()
    } catch (err) {
      notifyError(String(err))
    } finally {
      setActionLoading(false)
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'data_fmt',
      header: 'Dia',
      size: 110,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.data_fmt}</div>
          {row.original.alerta_conflito && (
            <Badge color="danger" className="mt-1" pill style={{ fontSize: '0.65rem' }}>Conflito</Badge>
          )}
          {!row.original.alerta_conflito && row.original.alerta_bloco_longo && (
            <Badge color="warning" className="mt-1" pill style={{ fontSize: '0.65rem' }}>Bloco &gt; 6h</Badge>
          )}
          {!row.original.alerta_conflito && !row.original.alerta_bloco_longo && row.original.alerta_limite_diario && (
            <Badge color="warning" className="mt-1" pill style={{ fontSize: '0.65rem' }}>&gt; 10h no dia</Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'entrada_fmt',
      header: 'Entrada',
      Cell: ({ cell }) => cell.getValue() || '—',
    },
    {
      accessorKey: 'saida_fmt',
      header: 'Saída',
      Cell: ({ cell }) => cell.getValue() || '—',
    },
    {
      accessorKey: 'tempo_dia_fmt',
      header: 'Tempo',
      size: 90,
    },
    {
      id: 'retroativos',
      header: 'Retroativos',
      size: 110,
      Cell: ({ row }) => {
        const qtd = row.original.retroativos || 0
        if (qtd > 0) {
          return (
            <Badge color="warning" pill>
              {qtd}
            </Badge>
          )
        }
        return '—'
      },
    },
  ], [])

  const historicoColumns = useMemo(() => [
    {
      accessorKey: 'mes_ref',
      header: 'Mês',
      size: 90,
      sortingFn: (rowA, rowB) => rowA.original.mes_ref.localeCompare(rowB.original.mes_ref),
      Cell: ({ row }) => (
        <Button
          color="link"
          className="p-0 text-left"
          style={{ fontSize: '11px' }}
          onClick={() => goToMes(row.original.mes_ref)}
        >
          {row.original.mes}
        </Button>
      ),
    },
    { accessorKey: 'carga_contratada_fmt', header: 'Carga contratada', size: 110 },
    { accessorKey: 'carga_realizada_fmt', header: 'Carga realizada', size: 110 },
    { accessorKey: 'abono_fmt', header: 'Abono', size: 80 },
    {
      accessorKey: 'saldo_mes_fmt',
      header: 'Saldo (fechamento)',
      size: 110,
      Cell: ({ row, cell }) => (
        <span style={saldoDivergenciaStyle(saldosDivergem(row.original))}>
          {cell.getValue() || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'saldo_calculado_fmt',
      header: 'Saldo (calculado)',
      size: 110,
      Cell: ({ row, cell }) => (
        <span style={saldoDivergenciaStyle(saldosDivergem(row.original))}>
          {cell.getValue() || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'banco_encerramento_fmt',
      header: 'Banco acumulado (encerramento)',
      size: 150,
      Cell: ({ cell }) => cell.getValue() || '—',
    },
    {
      accessorKey: 'banco_calculado_fmt',
      header: 'Banco acumulado (calculado)',
      size: 150,
      Cell: ({ row, cell }) => (
        <span style={saldoDivergenciaStyle(bancosDivergem(row.original))}>
          {cell.getValue() || '—'}
        </span>
      ),
    },
    {
      id: 'fechamento',
      header: 'Fechamento',
      size: 90,
      Cell: ({ row }) => (
        row.original.fechamento_ativo
          ? <Badge color="success" pill>Sim</Badge>
          : <Badge color="secondary" pill>Não</Badge>
      ),
    },
  ], [])

  const historicoMeses = historico?.meses || []
  const historicoResumo = historico?.resumo || null

  const historicoBancoCalculadoTotal = useMemo(() => {
    if (!historicoMeses.length) return null
    const latest = [...historicoMeses].sort((a, b) => String(b.mes_ref).localeCompare(String(a.mes_ref)))[0]
    return latest?.banco_calculado_fmt || null
  }, [historicoMeses])

  const resumoCardsTotais = useMemo(() => {
    if (viewMode === 'historico' && historicoResumo?.banco_acumulado_fmt) {
      return { ...totais, banco_acumulado_fmt: historicoResumo.banco_acumulado_fmt }
    }
    return totais
  }, [viewMode, totais, historicoResumo])

  const renderDetailPanel = ({ row }) => {
    const atividades = row.original.atividades || []
    if (!atividades.length) {
      return <div className="p-3 text-muted small">Nenhuma atividade neste dia.</div>
    }
    return (
      <div className="p-2" style={{ background: '#fafbfc' }}>
        <MaterialReactTable
          columns={[
            {
              accessorKey: 'projeto_nome',
              header: 'Atividade',
              Cell: ({ row: r }) => (
                <div>
                  <div>{r.original.pontoAtiv_desc || r.original.projeto_nome || '—'}</div>
                  {r.original.is_retroativo && (
                    <Badge color="warning" className="mt-1" pill style={{ fontSize: '0.65rem' }}>Retroativo</Badge>
                  )}
                </div>
              ),
            },
            { accessorKey: 'pontoAtiv_tempo', header: 'Tempo', size: 80 },
            {
              id: 'status',
              header: 'Status',
              size: 100,
              Cell: ({ row: r }) => formatAprovBadge(r.original.pontoAtiv_aprov),
            },
            {
              id: 'acoes',
              header: 'Ações',
              size: 160,
              Cell: ({ row: r }) => {
                if (!r.original.pendente) return '—'
                return (
                  <div className="d-flex" style={{ gap: 4 }}>
                    <Button
                      size="sm"
                      color="success"
                      disabled={updateSolicitationIsLoading}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAprovarAtiv(r.original)
                      }}
                    >
                      Aprovar
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      disabled={updateSolicitationIsLoading}
                      onClick={(e) => {
                        e.stopPropagation()
                        openRejectAtiv(r.original)
                      }}
                    >
                      Recusar
                    </Button>
                  </div>
                )
              },
            },
          ]}
          data={atividades}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enablePagination={false}
          enableColumnActions={false}
          enableSorting={false}
          localization={MRT_Localization_PT}
          {...mrtCompactProps}
          muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
        />
      </div>
    )
  }

  return (
    <>
      <Modal isOpen={rejectOpen} toggle={() => setRejectOpen(false)} centered>
        <ModalHeader toggle={() => setRejectOpen(false)}>Recusar horas</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Motivo</Label>
            <Input
              type="text"
              maxLength={500}
              value={rejectReason}
              onChange={e => {
                setRejectReason(e.target.value)
                setRejectReasonError('')
              }}
            />
            {rejectReasonError && <span className="text-danger small">{rejectReasonError}</span>}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRejectOpen(false)}>Voltar</Button>
          <Button color="danger" disabled={updateSolicitationIsLoading} onClick={handleRejectConfirm}>
            Recusar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={feriasModalOpen} toggle={() => setFeriasModalOpen(false)} centered>
        <ModalHeader toggle={() => setFeriasModalOpen(false)}>Registrar férias</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Data inicial</Label>
            <Input type="date" value={feriasIni} onChange={e => setFeriasIni(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Data final</Label>
            <Input type="date" value={feriasFim} onChange={e => setFeriasFim(e.target.value)} />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Justificativa (opcional)</Label>
            <Input type="text" maxLength={500} value={feriasJustif} onChange={e => setFeriasJustif(e.target.value)} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setFeriasModalOpen(false)}>Cancelar</Button>
          <Button color="primary" disabled={actionLoading} onClick={handleRegistrarFerias}>
            Registrar
            {actionLoading && <Spinner size="sm" className="ml-2" />}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={feriadoModalOpen} toggle={() => setFeriadoModalOpen(false)} centered>
        <ModalHeader toggle={() => setFeriadoModalOpen(false)}>Registrar feriado</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Data do feriado</Label>
            <Input type="date" value={feriadoData} onChange={e => setFeriadoData(e.target.value)} />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Nome do feriado</Label>
            <Input
              type="text"
              maxLength={200}
              value={feriadoNome}
              onChange={e => setFeriadoNome(e.target.value)}
              placeholder="Ex.: Natal, Ano Novo"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setFeriadoModalOpen(false)}>Cancelar</Button>
          <Button color="primary" disabled={actionLoading} onClick={handleRegistrarFeriado}>
            Registrar
            {actionLoading && <Spinner size="sm" className="ml-2" />}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={atestadoModalOpen} toggle={() => setAtestadoModalOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setAtestadoModalOpen(false)}>Registrar atestado</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Data inicial</Label>
                <Input type="date" value={atestadoIni} onChange={e => setAtestadoIni(e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Data final</Label>
                <Input type="date" value={atestadoFim} onChange={e => setAtestadoFim(e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          {atestadoMesmoDia && (
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Hora inicial</Label>
                  <Input type="time" value={atestadoHoraIni} onChange={e => setAtestadoHoraIni(e.target.value)} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Hora final</Label>
                  <Input type="time" value={atestadoHoraFim} onChange={e => setAtestadoHoraFim(e.target.value)} />
                </FormGroup>
              </Col>
            </Row>
          )}
          <FormGroup>
            <Label>Justificativa</Label>
            <Input
              type="textarea"
              rows={2}
              maxLength={500}
              value={atestadoJustif}
              onChange={e => setAtestadoJustif(e.target.value)}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Anexo (opcional)</Label>
            <Input
              type="file"
              multiple
              onChange={e => setAtestadoFiles(Array.from(e.target.files || []))}
            />
            {atestadoFiles.length > 0 && (
              <div className="text-muted small mt-1">
                {atestadoFiles.map(f => f.name).join(', ')}
              </div>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setAtestadoModalOpen(false)}>Cancelar</Button>
          <Button color="primary" disabled={actionLoading} onClick={handleRegistrarAtestado}>
            Registrar
            {actionLoading && <Spinner size="sm" className="ml-2" />}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={ajusteModalOpen} toggle={() => setAjusteModalOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setAjusteModalOpen(false)}>Ajuste de ponto</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Data</Label>
                <Input type="date" value={ajusteData} onChange={e => setAjusteData(e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Entrada</Label>
                <Input type="time" value={ajusteEntrada} onChange={e => setAjusteEntrada(e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Saída</Label>
                <Input type="time" value={ajusteSaida} onChange={e => setAjusteSaida(e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Justificativa</Label>
            <Input
              type="textarea"
              rows={2}
              maxLength={500}
              value={ajusteJustif}
              onChange={e => setAjusteJustif(e.target.value)}
            />
          </FormGroup>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Label className="mb-0">Atividades</Label>
            <Button size="sm" color="light" onClick={addAjusteRow}>+ atividade</Button>
          </div>
          {ajusteAtivRows.map((row, idx) => (
            <div key={row.id} className="border rounded p-2 mb-2" style={{ background: '#fafbfc' }}>
              <Row>
                <Col md={6}>
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">Setor / projeto</Label>
                    <Input
                      type="select"
                      value={row.projeto_id}
                      onChange={e => loadAtividadesAjusteRow(row.id, e.target.value)}
                    >
                      <option value="">— selecione —</option>
                      {ajusteProjetos.map(p => (
                        <option key={p.id} value={p.id}>{p.nome}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">Atividade</Label>
                    <Input
                      type="select"
                      value={row.atividade}
                      disabled={!row.projeto_id}
                      onChange={e => updateAjusteRow(row.id, { atividade: e.target.value })}
                    >
                      <option value="">— selecione —</option>
                      {row.atividadesOpts.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={7}>
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">Descrição</Label>
                    <Input
                      type="text"
                      value={row.descricao}
                      onChange={e => updateAjusteRow(row.id, { descricao: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">Tempo</Label>
                    <Input
                      type="text"
                      placeholder="08:00:00"
                      value={row.tempo}
                      onChange={e => updateAjusteRow(row.id, { tempo: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    size="sm"
                    color="danger"
                    outline
                    className="mb-2"
                    disabled={ajusteAtivRows.length <= 1 && idx === 0}
                    onClick={() => removeAjusteRow(row.id)}
                  >
                    Remover
                  </Button>
                </Col>
              </Row>
            </div>
          ))}
          <p className="text-muted small mb-0">
            O ajuste é registrado como retroativo e ficará pendente de aprovação.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setAjusteModalOpen(false)}>Cancelar</Button>
          <Button color="primary" disabled={actionLoading} onClick={handleRegistrarAjuste}>
            Registrar
            {actionLoading && <Spinner size="sm" className="ml-2" />}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={fecharCargaModalOpen} toggle={() => setFecharCargaModalOpen(false)}>
        <ModalHeader toggle={() => setFecharCargaModalOpen(false)}>
          Primeira folha — carga horária
        </ModalHeader>
        <ModalBody>
          {(() => {
            const info = extrato?.totais?.primeira_folha_carga_reduzida || {}
            return (
              <>
                <p className="mb-2">
                  O primeiro registro de ponto deste colaborador em {mesMmYyyy} foi em{' '}
                  <strong>{info.primeiro_ponto_fmt || '—'}</strong> (após o dia 2) e não há folhas
                  fechadas em meses anteriores.
                </p>
                <p className="text-muted small mb-3">
                  Carga contratual do contrato: <strong>{info.carga_contratual_fmt || '—'}</strong>
                  {' '}(máx. {info.carga_contratual_hhmm || '—'}).
                </p>
                <FormGroup check className="mb-3">
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={fecharCargaUsarReduzida}
                      onChange={(e) => {
                        setFecharCargaUsarReduzida(e.target.checked)
                        setFecharCargaErro('')
                        if (!e.target.checked) setFecharCargaHhmm('')
                      }}
                    />
                    {' '}Usar carga horária contratada menor que a do contrato neste fechamento
                  </Label>
                </FormGroup>
                {fecharCargaUsarReduzida && (
                  <FormGroup>
                    <Label for="fecharCargaHhmm">Carga horária do mês (HH:MM)</Label>
                    <Input
                      id="fecharCargaHhmm"
                      value={fecharCargaHhmm}
                      placeholder={`00:00 a ${info.carga_contratual_hhmm || '176:00'}`}
                      onChange={(e) => {
                        setFecharCargaHhmm(e.target.value)
                        setFecharCargaErro('')
                      }}
                    />
                    <small className="text-muted">
                      Informe um valor entre 00:00 e {info.carga_contratual_hhmm || '—'}.
                    </small>
                  </FormGroup>
                )}
                {fecharCargaErro && (
                  <Alert color="danger" className="py-2 mb-0">{fecharCargaErro}</Alert>
                )}
              </>
            )
          })()}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setFecharCargaModalOpen(false)}>Cancelar</Button>
          <Button color="primary" disabled={actionLoading} onClick={confirmarFechamentoComCarga}>
            Continuar fechamento
            {actionLoading && <Spinner size="sm" className="ml-2" />}
          </Button>
        </ModalFooter>
      </Modal>

      <ResumoCards
        totais={resumoCardsTotais}
        loading={loading || (viewMode === 'historico' && historicoLoading)}
      />

      <div
        className="d-flex flex-wrap align-items-center justify-content-between mb-3"
        style={{ gap: 10 }}
      >
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
          <Button size="sm" color="light" onClick={() => shiftMes(-1)} disabled={loading || viewMode === 'historico'}>
            <i className="ri-arrow-left-s-line" />
          </Button>
          <input
            type="month"
            value={extratoMesRef || ''}
            onChange={e => {
              setExtratoMesRef(e.target.value)
              setViewMode('extrato')
            }}
            disabled={loading || viewMode === 'historico'}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ced4da' }}
          />
          <Button size="sm" color="light" onClick={() => shiftMes(1)} disabled={loading || viewMode === 'historico'}>
            <i className="ri-arrow-right-s-line" />
          </Button>
          <span className="text-muted small">{mesMmYyyy}</span>
          {!loading && fechamentoAtivo && (
            <Badge color="info" pill>Ponto fechado</Badge>
          )}
          {!loading && !fechamentoAtivo && viewMode === 'extrato' && (
            <Badge color="warning" pill>Sem fechamento</Badge>
          )}
          {!loading && pendentesMes > 0 && viewMode === 'extrato' && (
            <Badge color="warning" pill>
              {pendentesMes}
              {' '}
              pend.
            </Badge>
          )}
        </div>

        <div className="d-flex flex-wrap align-items-center" style={{ gap: 6 }}>
          <Button
            size="sm"
            color={viewMode === 'historico' ? 'primary' : 'secondary'}
            outline={viewMode !== 'historico'}
            disabled={actionLoading}
            onClick={() => setViewMode(viewMode === 'historico' ? 'extrato' : 'historico')}
          >
            {viewMode === 'historico' ? 'Voltar ao extrato' : 'Histórico de meses'}
          </Button>
          <UncontrolledDropdown>
            <DropdownToggle
              size="sm"
              color="secondary"
              caret
              disabled={actionLoading || updateSolicitationIsLoading || viewMode === 'historico'}
            >
              Ferramentas do administrador
            </DropdownToggle>
            <DropdownMenu>
              {viewMode === 'extrato' && pendentesMes > 0 && (
                <DropdownItem
                  disabled={actionLoading || updateSolicitationIsLoading}
                  onClick={handleAprovarTodasPendencias}
                >
                  Aprovar pendências do mês (
                  {pendentesMes}
                  )
                </DropdownItem>
              )}
              {viewMode === 'extrato' && !fechamentoAtivo && (
                <DropdownItem disabled={actionLoading} onClick={() => runFechamento(false)}>
                  Fechar ponto
                </DropdownItem>
              )}
              {viewMode === 'extrato' && fechamentoAtivo && (
                <DropdownItem disabled={actionLoading} onClick={() => runFechamento(true)}>
                  Reprocessar fechamento
                </DropdownItem>
              )}
              {viewMode === 'extrato' && fechamentoAtivo && (
                <DropdownItem disabled={actionLoading} onClick={runCancelarFechamento}>
                  Cancelar fechamento
                </DropdownItem>
              )}
              {viewMode === 'extrato' && (
                <>
                  <DropdownItem divider />
                  <DropdownItem disabled={actionLoading} onClick={() => setFeriasModalOpen(true)}>
                    Lançar férias
                  </DropdownItem>
                  <DropdownItem disabled={actionLoading} onClick={openFeriadoModal}>
                    Lançar feriado
                  </DropdownItem>
                  <DropdownItem disabled={actionLoading} onClick={openAtestadoModal}>
                    Lançar atestado
                  </DropdownItem>
                  <DropdownItem disabled={actionLoading} onClick={openAjusteModal}>
                    Registrar ajuste de ponto
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>
          {actionLoading && <Spinner size="sm" color="primary" />}
        </div>
      </div>

      {viewMode === 'historico' && (
        <>
          {historicoLoading && (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          )}
          {!historicoLoading && historicoError && <Alert color="danger">{historicoError}</Alert>}
          {!historicoLoading && !historicoError && historicoMeses.length === 0 && (
            <p className="text-muted mb-0">Nenhum registro de ponto encontrado.</p>
          )}
          {!historicoLoading && !historicoError && historicoMeses.length > 0 && (
            <>
              <MaterialReactTable
                columns={historicoColumns}
                data={historicoMeses}
                getRowId={row => row.mes_ref}
                initialState={{
                  density: 'compact',
                  pagination: { pageIndex: 0, pageSize: 24 },
                  sorting: [{ id: 'mes_ref', desc: true }],
                }}
                enablePagination={historicoMeses.length > 12}
                enableGlobalFilter
                localization={MRT_Localization_PT}
                {...mrtCompactProps}
                muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
              />
              {historicoResumo && (
                <div
                  className="d-flex flex-wrap align-items-center px-2 py-2 border border-top-0"
                  style={{
                    gap: 8,
                    fontSize: '11px',
                    fontWeight: 700,
                    backgroundColor: '#f8f9fa',
                    borderColor: '#eef0f3',
                  }}
                >
                  <span style={{ minWidth: 90 }}>Total</span>
                  <span style={{ minWidth: 110 }}>{historicoResumo.carga_contratada_fmt}</span>
                  <span style={{ minWidth: 110 }}>{historicoResumo.carga_realizada_fmt}</span>
                  <span style={{ minWidth: 80 }}>—</span>
                  <span style={{ minWidth: 110 }}>—</span>
                  <span style={{ minWidth: 110 }}>—</span>
                  <span style={{ minWidth: 150 }}>{historicoResumo.banco_acumulado_fmt}</span>
                  <span style={{ minWidth: 150 }}>{historicoBancoCalculadoTotal || '—'}</span>
                  <span style={{ minWidth: 90 }}>—</span>
                </div>
              )}
            </>
          )}
        </>
      )}

      {viewMode === 'extrato' && loading && (
        <div className="text-center py-4"><Spinner color="primary" /></div>
      )}
      {viewMode === 'extrato' && !loading && error && <Alert color="danger">{error}</Alert>}
      {viewMode === 'extrato' && !loading && !error && dias.length === 0 && (
        <p className="text-muted mb-0">Nenhum ponto ou atividade registrado neste mês.</p>
      )}
      {viewMode === 'extrato' && !loading && !error && dias.length > 0 && (
        <MaterialReactTable
          columns={columns}
          data={dias}
          getRowId={row => row.data}
          initialState={{
            density: 'compact',
            pagination: { pageIndex: 0, pageSize: 31 },
            sorting: [{ id: 'data_fmt', desc: true }],
          }}
          enableExpanding
          enableExpandAll={false}
          renderDetailPanel={renderDetailPanel}
          enableColumnResizing
          enableGlobalFilter
          enablePagination={dias.length > 10}
          localization={MRT_Localization_PT}
          {...mrtCompactProps}
          muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
        />
      )}
    </>
  )
}

const mapStateToProps = state => ({
  updateSolicitationIsLoading: state.humanResources.updateSolicitationIsLoading,
})

export default connect(mapStateToProps, { updateSolicitation })(RhExtratoPanel)
